import { type Static, Type } from "typebox";

import {
  MaintenanceBaselineGap,
  MaintenanceBaselineSource,
  MaintenanceDueStateName,
  MaintenanceRuleState,
  MeterSource,
  ServiceRecordState,
} from "./enums.js";
import { defineRoute } from "./route.js";
import {
  errors,
  Exact,
  Identifier,
  ListOf,
  Nullable,
  StringEnum,
  Timestamp,
} from "./schema.js";

/**
 * Where a rule stands. Which of the optional figures are present depends on
 * what the rule measures: miles, days, or both.
 */
export const MaintenanceDueState = Exact({
  daysRemaining: Type.Optional(Type.Integer()),
  dueAtDate: Type.Optional(Timestamp),
  dueAtOdometerMiles: Type.Optional(Type.Integer()),
  milesRemaining: Type.Optional(Type.Integer()),
  /** Present when the rule, or one half of it, has nothing to count from. */
  missingBaselines: Type.Optional(Type.Array(MaintenanceBaselineGap)),
  state: MaintenanceDueStateName,
});
export type MaintenanceDueState = Static<typeof MaintenanceDueState>;

/** What a rule counts from, and how anyone knows. */
export const MaintenanceBaseline = Exact({
  date: Nullable(Timestamp),
  /** The receipt attached to a past service entered by hand. */
  fileId: Nullable(Identifier),
  note: Nullable(Type.String()),
  odometerMiles: Nullable(Type.Integer()),
  recordedAt: Nullable(Timestamp),
  /** The service that set it, when one did. */
  serviceRecordId: Nullable(Identifier),
  source: MaintenanceBaselineSource,
});
export type MaintenanceBaseline = Static<typeof MaintenanceBaseline>;

export const MaintenanceItem = Exact({
  baseline: MaintenanceBaseline,
  dueState: MaintenanceDueState,
  id: Identifier,
  intervalDays: Nullable(Type.Integer()),
  intervalMiles: Nullable(Type.Integer()),
  name: Type.String(),
  state: MaintenanceRuleState,
  vehicleId: Identifier,
  version: Type.Integer(),
});
export type MaintenanceItem = Static<typeof MaintenanceItem>;

export const VehicleMaintenance = Exact({
  items: Type.Array(MaintenanceItem),
  /** The most pressing state across the vehicle's rules. */
  summary: MaintenanceDueStateName,
});
export type VehicleMaintenance = Static<typeof VehicleMaintenance>;

export const FleetMaintenanceVehicle = Exact({
  dueStates: Type.Array(MaintenanceItem),
  internalLabel: Type.String(),
  /** The newest reading still standing, which mileage is measured against. */
  latestReading: Nullable(
    Exact({
      observedAt: Timestamp,
      odometerMiles: Type.Integer(),
      source: MeterSource,
    }),
  ),
  summary: MaintenanceDueStateName,
  vehicleId: Identifier,
});
export type FleetMaintenanceVehicle = Static<typeof FleetMaintenanceVehicle>;

export const FleetMaintenance = Exact({
  /** Vehicles per summary state; a state no vehicle is in is absent. */
  counts: Type.Record(Type.String(), Type.Integer()),
  vehicles: Type.Array(FleetMaintenanceVehicle),
});
export type FleetMaintenance = Static<typeof FleetMaintenance>;

export const MaintenanceTemplate = Exact({
  archivedAt: Nullable(Timestamp),
  id: Identifier,
  intervalDays: Nullable(Type.Integer()),
  intervalMiles: Nullable(Type.Integer()),
  name: Type.String(),
});
export type MaintenanceTemplate = Static<typeof MaintenanceTemplate>;

export const MaintenanceTemplateList = ListOf(MaintenanceTemplate);
export type MaintenanceTemplateList = Static<typeof MaintenanceTemplateList>;

/**
 * Intervals are bounded here as well as by the database's check constraints.
 * The constraint guarantees the data; this produces a message someone can act
 * on instead of a driver error. `minimum: 1` is the important half: a zero
 * interval is a typo, and a rule carrying one would be due forever.
 */
const Interval = {
  baselineDate: Nullable(Type.String({ format: "date-time" })),
  baselineOdometerMiles: Nullable(
    Type.Integer({ maximum: 10_000_000, minimum: 0 }),
  ),
  intervalDays: Nullable(Type.Integer({ maximum: 36_500, minimum: 1 })),
  intervalMiles: Nullable(Type.Integer({ maximum: 1_000_000, minimum: 1 })),
};

const RuleName = Type.String({ maxLength: 120, minLength: 1 });

/**
 * Where a rule counts from. Always said explicitly: "not sure yet" is a
 * choice, and so is "from today". A past service needs a date, an odometer,
 * or both.
 */
const BaselineChoice = {
  baselineDate: Type.Optional(Interval.baselineDate),
  baselineFileId: Type.Optional(Nullable(Identifier)),
  baselineNote: Type.Optional(Nullable(Type.String({ maxLength: 1000 }))),
  baselineOdometerMiles: Type.Optional(Interval.baselineOdometerMiles),
  /** Also save the odometer as a reading on that date. Off by default. */
  recordReading: Type.Optional(Type.Boolean()),
};

const Version = Type.Integer({ minimum: 1 });

export const listMaintenanceTemplates = defineRoute({
  method: "GET",
  path: "/organizations/:organizationId/maintenance/templates",
  response: { 200: MaintenanceTemplateList, ...errors(401, 403) },
  tags: ["maintenance"],
});

export const createMaintenanceTemplate = defineRoute({
  body: Exact({
    intervalDays: Type.Optional(Interval.intervalDays),
    intervalMiles: Type.Optional(Interval.intervalMiles),
    name: RuleName,
  }),
  method: "POST",
  path: "/organizations/:organizationId/maintenance/templates",
  response: { 201: MaintenanceTemplate, ...errors(401, 403, 422) },
  tags: ["maintenance"],
});

export const updateMaintenanceTemplate = defineRoute({
  body: Exact({
    intervalDays: Type.Optional(Interval.intervalDays),
    intervalMiles: Type.Optional(Interval.intervalMiles),
    name: Type.Optional(RuleName),
  }),
  method: "PATCH",
  path: "/organizations/:organizationId/maintenance/templates/:templateId",
  response: { 200: MaintenanceTemplate, ...errors(401, 403, 404, 422) },
  tags: ["maintenance"],
});

export const Archived = Exact({ id: Identifier });
export type Archived = Static<typeof Archived>;

export const archiveMaintenanceTemplate = defineRoute({
  method: "POST",
  path: "/organizations/:organizationId/maintenance/templates/:templateId/archive",
  response: { 200: Archived, ...errors(401, 403, 404) },
  tags: ["maintenance"],
});

export const getFleetMaintenance = defineRoute({
  method: "GET",
  path: "/organizations/:organizationId/maintenance",
  response: { 200: FleetMaintenance, ...errors(401, 403) },
  tags: ["maintenance"],
});

export const listVehicleMaintenance = defineRoute({
  method: "GET",
  path: "/organizations/:organizationId/vehicles/:vehicleId/maintenance",
  response: { 200: VehicleMaintenance, ...errors(401, 403) },
  tags: ["maintenance"],
});

export const createMaintenanceRule = defineRoute({
  body: Exact({
    ...BaselineChoice,
    baselineSource: StringEnum(["historical_entry", "rule_start", "unknown"]),
    intervalDays: Type.Optional(Interval.intervalDays),
    intervalMiles: Type.Optional(Interval.intervalMiles),
    name: RuleName,
    templateId: Type.Optional(Nullable(Type.String())),
  }),
  method: "POST",
  path: "/organizations/:organizationId/vehicles/:vehicleId/maintenance",
  response: {
    201: Exact({ id: Identifier }),
    // 409 when the vehicle has been sold or archived.
    ...errors(401, 403, 404, 409, 422),
  },
  tags: ["maintenance"],
});

export const MaintenanceRuleVersion = Exact({
  id: Identifier,
  version: Type.Integer(),
});
export type MaintenanceRuleVersion = Static<typeof MaintenanceRuleVersion>;

/** One interval a rule has had. Versions are never edited. */
export const MaintenanceIntervalVersion = Exact({
  changedAt: Timestamp,
  changedByName: Nullable(Type.String()),
  graceDays: Nullable(Type.Integer()),
  graceMiles: Nullable(Type.Integer()),
  intervalDays: Nullable(Type.Integer()),
  intervalMiles: Nullable(Type.Integer()),
  intervalMonths: Nullable(Type.Integer()),
  leadDays: Nullable(Type.Integer()),
  leadMiles: Nullable(Type.Integer()),
  reason: Nullable(Type.String()),
  version: Type.Integer(),
});
export type MaintenanceIntervalVersion = Static<
  typeof MaintenanceIntervalVersion
>;

/** A service that completed the rule, with the interval it was done under. */
export const MaintenanceServiceEvent = Exact({
  completedAt: Timestamp,
  completedOdometerMiles: Type.Integer(),
  /** The interval of that version; null when the version is not known. */
  intervalDays: Nullable(Type.Integer()),
  intervalMiles: Nullable(Type.Integer()),
  serviceRecordId: Identifier,
  /** Corrected services stay listed, marked, as the history they are. */
  state: ServiceRecordState,
  taskId: Identifier,
  version: Nullable(Type.Integer()),
});
export type MaintenanceServiceEvent = Static<typeof MaintenanceServiceEvent>;

export const MaintenanceRuleDetail = Exact({
  rule: MaintenanceItem,
  serviceEvents: Type.Array(MaintenanceServiceEvent),
  /** Newest first. */
  versions: Type.Array(MaintenanceIntervalVersion),
});
export type MaintenanceRuleDetail = Static<typeof MaintenanceRuleDetail>;

export const getMaintenanceRule = defineRoute({
  method: "GET",
  path: "/organizations/:organizationId/vehicles/:vehicleId/maintenance/:ruleId",
  response: { 200: MaintenanceRuleDetail, ...errors(401, 403, 404) },
  tags: ["maintenance"],
});

export const updateMaintenanceRule = defineRoute({
  body: Exact({
    intervalDays: Type.Optional(Interval.intervalDays),
    intervalMiles: Type.Optional(Interval.intervalMiles),
    name: Type.Optional(RuleName),
    /** Why the interval changed, kept in its history. */
    reason: Type.Optional(Nullable(Type.String({ maxLength: 500 }))),
    version: Version,
  }),
  method: "PATCH",
  path: "/organizations/:organizationId/vehicles/:vehicleId/maintenance/:ruleId",
  response: {
    200: MaintenanceRuleVersion,
    ...errors(401, 403, 404, 409, 422),
  },
  tags: ["maintenance"],
});

/**
 * Enters when a rule was last done (a past service, with its receipt if
 * there is one), or starts counting it from today. Either moves the rule out
 * of "needs setup".
 */
export const recordMaintenanceBaseline = defineRoute({
  body: Exact({
    ...BaselineChoice,
    baselineSource: StringEnum(["historical_entry", "rule_start"]),
    version: Version,
  }),
  method: "POST",
  path: "/organizations/:organizationId/vehicles/:vehicleId/maintenance/:ruleId/baseline",
  response: {
    200: MaintenanceRuleVersion,
    ...errors(401, 403, 404, 409, 422),
  },
  tags: ["maintenance"],
});

export const setMaintenanceRuleState = defineRoute({
  body: Exact({ state: MaintenanceRuleState, version: Version }),
  method: "POST",
  path: "/organizations/:organizationId/vehicles/:vehicleId/maintenance/:ruleId/state",
  response: {
    200: Exact({ id: Identifier, state: MaintenanceRuleState }),
    ...errors(401, 403, 404, 409),
  },
  tags: ["maintenance"],
});

export const archiveMaintenanceRule = defineRoute({
  body: Exact({ version: Version }),
  method: "POST",
  path: "/organizations/:organizationId/vehicles/:vehicleId/maintenance/:ruleId/archive",
  response: { 200: Archived, ...errors(401, 403, 404, 409) },
  tags: ["maintenance"],
});
